import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { useStore } from '../../store';
import { useTranslation } from 'react-i18next';
import { StepNav } from './step-nav/step-nav';
import { observer } from 'mobx-react';
import { StepFooter } from './step-footer/step-footer';
import { IStep, IStepControl, Step } from '../../domains/wizard';
import { SideQuoteInfo } from '../side-quote-info/side-quote-info';
import OnboardingInformation from './onboarding-information-step';
import DelegationInformation from './delegation-information';
import ServiceAddress from './service-address';
import BillingInformation from './billing-information';
import PaymentInformation from './payment-information';
import ReviewAndSignUp from './review';
import ShippingInformation from './shipping-information';
import { useErrorFieldContext } from '../common/error-field-context';
import { useNavigate } from 'react-router-dom';
import { ErrorPanel } from '../error-panel/error-panel';
import { LoaderComponent } from 'n2p-ui-library';

export const Wizard = observer(() => {
  const { t } = useTranslation();
  const { scrollToFirstError } = useErrorFieldContext();
  const { signUpStore, quoteStore, tenantStore } = useStore();
  const { activeStepIndex, errorCode } = signUpStore;
  const { isLoading } = quoteStore;
  const navigate = useNavigate();

  const tabs: { [K in number]: string } = useMemo(() => t('TAB', { returnObjects: true }), [tenantStore.config?.language, t]);
  const { stepsMap, lastStepIndex } = useMemo(() => {
    const stepsMap: IStep[] = [];
    if (quoteStore.isOnboardingStepAvailable) {
      stepsMap.push({ key: Step.Onboarding, index: stepsMap.length, title: tabs[Step.Onboarding] });
    }
    if (quoteStore.isDelegateStepAvailable) {
      stepsMap.push({ key: Step.Delegate, index: stepsMap.length, title: tabs[Step.Delegate] });
    }
    if (quoteStore.isServiceStepAvailable) {
      stepsMap.push({ key: Step.Service, index: stepsMap.length, title: tabs[Step.Service] });
    }
    if (quoteStore.isShippingStepAvailable) {
      stepsMap.push({ key: Step.Shipping, index: stepsMap.length, title: tabs[Step.Shipping] });
    }
    if (quoteStore.isBillingStepAvailable) {
      stepsMap.push({ key: Step.Billing, index: stepsMap.length, title: tabs[Step.Billing] });
    }
    if (quoteStore.isPaymentStepAvailable) {
      stepsMap.push({ key: Step.Payment, index: stepsMap.length, title: tabs[Step.Payment] });
    }
    stepsMap.push({ key: Step.Review, index: stepsMap.length, title: tabs[Step.Review] });

    return { stepsMap, lastStepIndex: stepsMap.length - 1 };
  }, [quoteStore.quoteInfo, tabs]);

  const setActiveStep = useCallback(
    async (index: number) => {
      if (index < signUpStore.activeStepIndex) {
        signUpStore.activeStepIndex = index;
        window.scrollTo(0, 0);
        return;
      }

      if (await signUpStore.isStepValid(stepsMap[signUpStore.activeStepIndex].key)) {
        stepsMap[signUpStore.activeStepIndex].completed = true;
        if (index > lastStepIndex) {
          await signUpStore.signUp();
          if (!signUpStore.errorCode) navigate('/success');
          return;
        }

        signUpStore.activeStepIndex = index;
        window.scrollTo(0, 0);
        signUpStore.showErrors = false;
        return;
      }

      signUpStore.showErrors = true;
      scrollToFirstError();
    },
    [signUpStore, stepsMap],
  );
  const isAllCompleted = useCallback((): boolean => stepsMap.every(x => x.completed || x.index === stepsMap.length - 1), [stepsMap]);

  const stepControl: IStepControl = {
    activeStepIndex,
    setActiveStep,
    lastStepIndex: lastStepIndex,
    isAllCompleted,
    stepsMap,
  };

  const activeStepKey = stepsMap[activeStepIndex].key;
  return (
    <>
      {errorCode ? (
        <ErrorPanel code={errorCode} />
      ) : isLoading ? (
        <div className="quote-info">
          {stepsMap.length > 1 && (
            <div className="container-step-labels">
              {stepsMap.map((step, index) => {
                return <StepNav key={step.key} index={index} title={step.title} completed={step.completed} stepControl={stepControl} />;
              })}
            </div>
          )}

          <div className="container-steps">
            {activeStepIndex !== lastStepIndex && <SideQuoteInfo />}
            {activeStepKey === Step.Onboarding && <OnboardingInformation />}
            {activeStepKey === Step.Delegate && <DelegationInformation />}
            {activeStepKey === Step.Service && <ServiceAddress />}
            {activeStepKey === Step.Shipping && <ShippingInformation />}
            {activeStepKey === Step.Billing && <BillingInformation />}
            {activeStepKey === Step.Payment && <PaymentInformation />}
            {activeStepKey === Step.Review && <ReviewAndSignUp stepControl={stepControl} />}
            <div className="step">
              <StepFooter stepControl={stepControl} />
            </div>
          </div>
        </div>
      ) : (
        <div className={'quote-info'}>
          <LoaderComponent />
        </div>
      )}
    </>
  );
});
